/*
  planta-variant-picker
  ---------------------
  Custom variant picker for the Planta PDP. The UI is a set of <button
  role="radio"> pills grouped inside <div role="radiogroup">, matching the
  ecommerce pattern used by every modern storefront (Nike, Aritzia, SSENSE,
  etc.). Native radio inputs were swapped out because:

    - They force a visible circle-dot aesthetic that modern PDPs don't use.
    - Their selected state is tangled up with browser focus styling, which
      hurts the "clear solid fill on selected" pattern shoppers expect.
    - Buttons with aria-checked give us total visual control while keeping
      full accessibility (announced as a radio group to assistive tech).

  What this component owns:

    - Selection state (read/written via aria-checked="true")
    - Roving tabindex — only the selected pill in each group is in the
      tab order; within the group, arrow keys move focus/selection.
    - Keyboard: ArrowLeft/Right/Up/Down cycle, Home/End jump to ends.
    - Syncing the form's hidden <input name="id"> to the right variant id.
    - Updating the displayed price when variants have different prices.
    - Toggling the add-to-cart button between "Add to bag" / "Sold out".
    - Marking variants as .is-unavailable when the combination is sold out.
    - Keeping the URL's ?variant= parameter in sync (replaceState).
*/

class PlantaVariantPicker extends HTMLElement {
  connectedCallback() {
    this.variants = this.#readVariants();
    if (!this.variants.length) return;

    this.form = this.closest('form');
    this.idInput = this.form?.querySelector('input[name="id"]');
    this.section = this.closest('.planta-pdp-info');
    this.priceEl = this.section?.querySelector('.planta-pdp-info__price');
    this.button = this.form?.querySelector('.planta-pdp-info__add-btn');
    this.productUrl = this.dataset.productUrl || '';

    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeyDown);

    this.#syncFromCurrentSelection();
  }

  /*
    Reads the JSON blob embedded next to the picker. Defensive: if the
    script tag is missing or malformed, we log and bail so the page still
    renders — the buttons just won't become interactive.
  */
  #readVariants() {
    const dataEl = this.querySelector('[data-planta-variant-data]');
    if (!dataEl) return [];
    try {
      const parsed = JSON.parse(dataEl.textContent || '{}');
      return Array.isArray(parsed.variants) ? parsed.variants : [];
    } catch (err) {
      console.error('[planta-variant-picker] Failed to parse variant data', err);
      return [];
    }
  }

  #onClick = (event) => {
    const target = event.target.closest('[role="radio"]');
    if (!target || !this.contains(target)) return;
    this.#selectButton(target, { focus: false });
  };

  /*
    Keyboard handling for the radiogroup pattern.
    See https://www.w3.org/WAI/ARIA/apg/patterns/radio/ — this mirrors the
    spec: arrows cycle, Home/End jump. We skip buttons that are marked
    is-unavailable so keyboard users can't land on a sold-out combo.
  */
  #onKeyDown = (event) => {
    const current = event.target.closest('[role="radio"]');
    if (!current || !this.contains(current)) return;

    const group = current.closest('.planta-pdp-info__variants');
    if (!group) return;

    const siblings = this.#focusableButtons(group);
    if (!siblings.length) return;

    const idx = siblings.indexOf(current);
    let next = null;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = siblings[(idx + 1) % siblings.length];
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = siblings[(idx - 1 + siblings.length) % siblings.length];
        break;
      case 'Home':
        next = siblings[0];
        break;
      case 'End':
        next = siblings[siblings.length - 1];
        break;
      default:
        return;
    }

    event.preventDefault();
    if (next) this.#selectButton(next, { focus: true });
  };

  /*
    Returns the buttons in a group that a keyboard user is allowed to land
    on: currently, everything that's not .is-unavailable. If every variant
    in the group is unavailable we fall back to all of them so the group
    isn't a dead end.
  */
  #focusableButtons(group) {
    const all = Array.from(group.querySelectorAll('[role="radio"]'));
    const enabled = all.filter((btn) => !btn.classList.contains('is-unavailable'));
    return enabled.length ? enabled : all;
  }

  #selectButton(button, { focus }) {
    const group = button.closest('.planta-pdp-info__variants');
    if (!group) return;

    group.querySelectorAll('[role="radio"]').forEach((btn) => {
      const selected = btn === button;
      btn.setAttribute('aria-checked', selected ? 'true' : 'false');
      btn.setAttribute('tabindex', selected ? '0' : '-1');
    });

    if (focus) button.focus();

    this.#syncFromCurrentSelection();
  }

  /*
    Central update path. Reads every group's currently-selected button into
    a position-ordered array, finds the matching variant, and pushes all
    downstream UI updates in one place. Called from initial hydration, clicks,
    and keyboard selection alike.
  */
  #syncFromCurrentSelection() {
    const selectedValues = this.#getSelectedValues();
    const variant = this.#findVariant(selectedValues);

    this.#updateAvailabilityMarkers(selectedValues);

    if (variant) {
      this.#setHiddenId(variant.id);
      this.#updatePrice(variant);
      this.#updateButton(variant, selectedValues);
      this.#updateUrl(variant);
    } else {
      /*
        No variant matched. Wipe the hidden id so a stray submit can't post
        a stale variant from a previous selection — the button is disabled
        in this branch anyway, but belt-and-suspenders.
      */
      this.#setHiddenId('');
      this.#updateButton(null, selectedValues);
    }
  }

  /*
    Returns an array like ["Small", "Black"] in option-position order.
    Missing selections yield null at that index so partially-selected
    states are still reasoned about safely.
  */
  #getSelectedValues() {
    const groups = this.querySelectorAll('.planta-pdp-info__option');
    const values = [];
    groups.forEach((group) => {
      const idx = Number(group.dataset.optionIndex ?? 0);
      const selected = group.querySelector('[role="radio"][aria-checked="true"]');
      values[idx] = selected ? selected.dataset.optionValue : null;
    });
    return values;
  }

  #findVariant(selectedValues) {
    return (
      this.variants.find((variant) =>
        variant.options.every((opt, idx) => opt === selectedValues[idx])
      ) || null
    );
  }

  #setHiddenId(id) {
    if (!this.idInput) return;
    this.idInput.value = String(id);
    /*
      Dispatching change so anything else listening on the hidden input
      (analytics, sticky ATC, etc.) stays in sync without us having to
      know about every downstream consumer.
    */
    this.idInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  #updatePrice(variant) {
    if (!this.priceEl) return;
    if (variant.price_formatted) {
      this.priceEl.textContent = variant.price_formatted;
    }
  }

  /*
    Three disabled states the shopper can see on the add-to-cart button, in
    priority order:

      1. "Select a size" — at least one option group has no pick yet. This is
         the default on page load; we want the shopper to take an explicit
         action before the button lights up.
      2. "Unavailable"   — every option is picked, but the combination isn't a
         real variant (shouldn't happen with valid data, but we don't want to
         silently enable a broken submit if it does).
      3. "Sold out"      — valid variant, but out of stock.

    Only when we have a real, in-stock variant do we flip the button into its
    enabled "Add to bag" state.
  */
  #updateButton(variant, selectedValues = []) {
    if (!this.button) return;

    if (variant && variant.available) {
      this.button.removeAttribute('disabled');
      this.button.textContent = 'Add to bag';
      return;
    }

    this.button.setAttribute('disabled', 'disabled');

    if (!variant) {
      const hasMissingSelection = selectedValues.some((v) => v == null);
      this.button.textContent = hasMissingSelection ? 'Select a size' : 'Unavailable';
    } else {
      this.button.textContent = 'Sold out';
    }
  }

  /*
    For each button in each group, determine whether picking *that* value
    alongside the current selections in the other groups would land on a
    valid, available variant. If not, mark it .is-unavailable — the classic
    strikethrough pattern shoppers recognize (size still visible, but
    clearly flagged as not in stock).
  */
  #updateAvailabilityMarkers(selectedValues) {
    const groups = this.querySelectorAll('.planta-pdp-info__option');
    groups.forEach((group) => {
      const idx = Number(group.dataset.optionIndex ?? 0);
      const buttons = group.querySelectorAll('[role="radio"]');
      buttons.forEach((btn) => {
        const hypothetical = selectedValues.slice();
        hypothetical[idx] = btn.dataset.optionValue;
        const match = this.#findVariant(hypothetical);
        const unavailable = !match || !match.available;
        btn.classList.toggle('is-unavailable', unavailable);
      });
    });
  }

  /*
    Keep the URL honest so a shared link always loads the same variant
    the user was viewing. replaceState avoids cluttering history with
    every size click.
  */
  #updateUrl(variant) {
    if (!this.productUrl || !window.history?.replaceState) return;
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(variant.id));
      window.history.replaceState({}, '', url.toString());
    } catch (err) {
      /* no-op — URL updates are a nice-to-have, never worth breaking on */
    }
  }
}

if (!customElements.get('planta-variant-picker')) {
  customElements.define('planta-variant-picker', PlantaVariantPicker);
}
